import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { usePostHog } from './usePostHog';

/**
 * Hook to automatically track page views on route changes in SPAs
 * Should be used once at the router level
 */
// eslint-disable-next-line react-refresh/only-export-components -- Hook is co-exported with its component wrapper RoutePageTracker
export const useRouterPageTracking = () => {
  const location = useLocation();
  const { capturePageView } = usePostHog();

  useEffect(() => {
    // Generate a readable page name from the pathname
    const generatePageName = (pathname: string): string => {
      // Remove leading slash and replace slashes with spaces
      const cleanPath = pathname.replace(/^\//, '').replace(/\//g, ' ');

      // Handle common patterns
      if (!cleanPath || cleanPath === '') return 'Dashboard';
      if (cleanPath.startsWith('manage universities')) return 'Universities Management';
      if (cleanPath.includes('institutes')) return 'Institutes';
      if (cleanPath.includes('subjects')) return 'Subjects';
      if (cleanPath.includes('surveys')) return 'Surveys';
      if (cleanPath.includes('reports')) return 'Reports';
      if (cleanPath.includes('users')) return 'Users';
      if (cleanPath.includes('settings')) return 'Settings';
      if (cleanPath.includes('survey-audit')) return 'Survey Audit';

      // Capitalize first letter and replace hyphens with spaces
      return cleanPath
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).replace(/-/g, ' '))
        .join(' ');
    };

    const pageName = generatePageName(location.pathname);

    // Track page view with additional context
    capturePageView(pageName, {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      referrer: document.referrer,
    });
  }, [location.pathname, location.search, location.hash, capturePageView]);
};

/**
 * Component version of the page tracking hook
 * Use this if you prefer component-based approach
 */
export const RoutePageTracker: React.FC = () => {
  useRouterPageTracking();
  return null;
};
